/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import React, { useEffect, useState, useRef } from "react";
import { injectIntl } from "react-intl";
import * as stores from "stores";
import PopupCss from "./Popup.css";
import Cancel from "dc-icons/viewer2021/S_CrossMedium_18_N.svg";
import ButtonWrapper from "dc-preview-utils/src/components/Button/ButtonWrapper";
import { fetchCustomDialogComponent } from "utils/PopupUtils";
import { POPUP_POSITION } from "../../constants/enums";
import PropTypes from "prop-types";
import { isFunction } from "utils/APIUtils";

const initialDialogComponent = {
  CustomDialogComponent: null
};

const PopupView = props => {
  const {
    intl: { formatMessage },
    shouldShowPopup,
    popupConfig: {
      dropinId,
      position = POPUP_POSITION.TOP_RIGHT,
      onClose = () => {},
      hideOnCloseButtonClick = () => Promise.resolve(true)
    }
  } = props;
  const [dialogComponent, setDialogComponent] = useState(initialDialogComponent);
  const isCloseButtonClicked = useRef(false);
  const { CustomDialogComponent } = dialogComponent;
  // This flag is used to prevent state update if the component is unmounted during the async operation
  const isCancelled = useRef(false);

  useEffect(() => {
    if (dropinId && shouldShowPopup) {
      (async () => {
        const customDialogComponent = await fetchCustomDialogComponent(dropinId);
        if (!isCancelled.current && customDialogComponent) {
          setDialogComponent({
            CustomDialogComponent: customDialogComponent
          });
        }
      })();

      return () => {
        isCancelled.current = true;
        // Any cleanup code here
        if (isFunction(onClose)) {
          onClose();
        }
      };
    }
  }, [shouldShowPopup]);

  const customDialogComponent = () =>
    CustomDialogComponent && <CustomDialogComponent isCloseButtonClicked={() => isCloseButtonClicked.current} />;

  const contextMenuHandler = event => {
    event.stopPropagation();
  };

  const onCloseClickHandler = event => {
    if (isFunction(hideOnCloseButtonClick)) {
      return hideOnCloseButtonClick().then(shouldHide => {
        if (shouldHide) {
          hidePopup();
        } else {
          event.preventDefault();
          isCloseButtonClicked.current = false;
        }
      });
      // eslint-disable-next-line no-else-return
    } else {
      hidePopup();
      return Promise.resolve();
    }
  };

  const hidePopup = () => {
    isCloseButtonClicked.current = true;
    stores.appStore.setShouldShowPopup(false);
    stores.appStore.setPopupConfig({});
  };

  const positionStyle =
    position === POPUP_POSITION.BOTTOM_RIGHT ? { bottom: "20px", right: "20px" } : { top: "20px", right: "20px" };

  const positionStyleWithAnimation =
    position === POPUP_POSITION.BOTTOM_RIGHT
      ? { ...positionStyle, animation: `${PopupCss.slideUp} 0.6s ease-out` }
      : positionStyle;

  return shouldShowPopup && CustomDialogComponent ? (
    <div
      className={PopupCss.Popup}
      style={positionStyleWithAnimation}
      data-testid="customDialogPopup"
      ref={popupRef => {
        popupRef && popupRef.addEventListener("contextmenu", contextMenuHandler);
      }}
    >
      <div className={PopupCss.PopupHeader} data-testid="popupHeader">
        <div className={PopupCss.CloseButtonContainer}>
          <ButtonWrapper
            icon={Cancel}
            clickHandler={onCloseClickHandler}
            ariaLabel={formatMessage({ id: "arialabel.popup.close" })}
          />
        </div>
      </div>
      <div className={PopupCss.CustomDialogContent} data-testid="customDialogContent">
        {customDialogComponent()}
      </div>
    </div>
  ) : null;
};

PopupView.defaultProps = {
  shouldShowPopup: false
};

PopupView.prototype = {
  shouldShowPopup: PropTypes.bool,
  popupConfig: PropTypes.object
};

export default injectIntl(PopupView);
